'use client';

import { useState } from 'react';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import { IndustryCustomers } from '@/components/sections/industry-customers';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

export default function FinancialServicesPage() {
  const { t } = useTranslation();
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData = [
    {
      metric: t.industry['Data Security'],
      agent5: {
        value: t.industry['SOC 2, encryption at rest & transit'],
        status: 'best',
      },
      traditional: { value: t.industry['Varies by vendor'], status: 'worst' },
    },
    {
      metric: t.industry['Compliance'],
      agent5: { value: t.industry['GDPR, SOC 2, audit logs'], status: 'best' },
      traditional: { value: t.industry['Basic compliance'], status: 'neutral' },
    },
    {
      metric: t.industry['PII Handling'],
      agent5: { value: t.industry['Automatic redaction'], status: 'best' },
      traditional: { value: t.industry['Manual processes'], status: 'worst' },
    },
    {
      metric: t.industry['Response Accuracy'],
      agent5: { value: t.industry['95%+ verified responses'], status: 'best' },
      traditional: { value: t.industry['60-70% accuracy'], status: 'worst' },
    },
    {
      metric: t.industry['Fraud Prevention'],
      agent5: { value: t.industry['Real-time verification'], status: 'best' },
      traditional: { value: t.industry['Limited checks'], status: 'worst' },
    },
    {
      metric: t.industry['Audit Trail'],
      agent5: {
        value: t.industry['Complete conversation logs'],
        status: 'best',
      },
      traditional: { value: t.industry['Partial logging'], status: 'neutral' },
    },
    {
      metric: t.industry['Handoff to Human'],
      agent5: { value: t.industry['Seamless with context'], status: 'best' },
      traditional: { value: t.industry['Customer restarts'], status: 'worst' },
    },
    {
      metric: t.industry['Cost per Interaction'],
      agent5: {
        value: t.industry['$0.75-$0.90 per resolution'],
        status: 'best',
      },
      traditional: { value: t.industry['$5-15 per ticket'], status: 'worst' },
    },
  ];

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {t.industry['AI customer support for']} <br />
            {t.industry['for financial institutions']}
          </>
        }
        subtitle={
          t.industry[
            'Your customers trust you with their money. They deserve accurate, secure support. Agent 5 handles sensitive inquiries with the compliance and accuracy financial services demand—without the risk of traditional automation.'
          ]
        }
        ctaText={t.industry['See how it works for finance']}
      />

      <Logos />

      <BenefitsSection
        title={t.industry['Support your customers better, stay compliant']}
        subtitle={
          t.industry[
            'Handle the support issues that matter most—with the security financial services require.'
          ]
        }
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="#000000"
        benefits={[
          {
            title: t.industry['Account inquiries and balance checks'],
            description:
              t.industry[
                'Customers ask about balances, recent transactions, and account status. Agent 5 verifies identity, retrieves information securely, and responds instantly—24/7.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Card replacements and fraud reports'],
            description:
              t.industry[
                'Lost cards, suspicious charges, unauthorized transactions—Agent 5 handles reports, blocks cards, and initiates replacements while following your exact security protocols.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Payment and transfer questions'],
            description:
              t.industry[
                'Why was I charged? When will my transfer complete? Agent 5 explains fees, timelines, and payment status clearly—reducing confusion and calls to your team.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Login help and account access'],
            description:
              t.industry[
                'Forgot password? Locked out? Need to update contact info? Agent 5 guides customers through authentication, resets, and access issues—without compromising security.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Product recommendations that make sense'],
            description:
              t.industry[
                'Which savings account? Should I upgrade my card? Agent 5 helps customers understand their options based on their needs—personalized guidance, not pushy sales.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Transaction disputes and chargebacks'],
            description:
              t.industry[
                'Customers report disputed charges, request refunds, or ask about chargeback status. Agent 5 collects details, initiates investigations, and keeps customers informed throughout.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Compliance built-in, not bolted on'],
            description:
              t.industry[
                "Agent 5 won't share sensitive data inappropriately, give financial advice, or violate regulations. It verifies identity, logs every action, and escalates when needed. Your compliance team controls everything."
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '100%',
              label: t.industry['Audit Trail'],
              sublabel: t.industry['Every action logged'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.industry['Why financial institutions choose Agent 5']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.industry[
                  "Built specifically for regulated industries where accuracy and compliance aren't optional."
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.industry['What Matters in Finance']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.industry['Agent 5']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.industry['Traditional Solutions']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/50"
                      onMouseEnter={() => setHoveredModel(row.metric)}
                      onMouseLeave={() => setHoveredModel(null)}
                    >
                      <TableCell className="text-foreground font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-green-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.agent5.status === 'best' && (
                            <CheckCircle className="size-4 text-green-600" />
                          )}
                          {row.agent5.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-foreground text-sm">
                            {row.agent5.value}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-red-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.traditional.status === 'worst' && (
                            <XCircle className="size-4 text-red-600" />
                          )}
                          {row.traditional.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-muted-foreground text-sm">
                            {row.traditional.value}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.industry['Questions we hear from compliance teams']}
        subtitle={
          t.industry[
            'Direct answers to the concerns that matter most in financial services.'
          ]
        }
        items={[
          {
            question:
              t.industry['How do you handle PII and sensitive financial data?'],
            answer:
              t.industry[
                'All data is encrypted at rest (AES-256) and in transit (TLS 1.3). PII is automatically detected and can be redacted or masked. We maintain complete audit logs. Your data stays in your chosen region (US or EU). We never use customer data to train our models.'
              ],
          },
          {
            question: t.industry['What compliance certifications do you have?'],
            answer:
              t.industry[
                'SOC 2 Type II, GDPR compliant, working toward ISO 27001. We maintain detailed documentation for your auditors and can provide DPA agreements. EU data residency available (Frankfurt servers).'
              ],
          },
          {
            question:
              t.industry[
                'Will the AI give financial advice or make unauthorized transactions?'
              ],
            answer:
              t.industry[
                'No. Agent 5 is configured with strict guardrails. It cannot provide investment advice, approve transactions, or make decisions requiring human judgment. It handles information requests, account inquiries, and support issues—then escalates appropriately.'
              ],
          },
          {
            question:
              t.industry['How do you prevent fraud and account takeovers?'],
            answer:
              t.industry[
                'Agent 5 verifies customer identity before sharing sensitive information, integrates with your existing authentication systems, and flags suspicious patterns. When in doubt, it escalates to a human agent with full context.'
              ],
          },
          {
            question:
              t.industry[
                'Can we review and approve responses before they go out?'
              ],
            answer:
              t.industry[
                'Yes. You can configure Agent 5 to operate in supervised mode where responses require human approval, or set confidence thresholds for automatic vs. reviewed responses. Full customization based on your risk tolerance.'
              ],
          },
          {
            question: t.industry['What happens if the AI makes a mistake?'],
            answer:
              t.industry[
                'Every interaction is logged and reviewable. Mistakes are flagged in our QA system, and we provide correction tools. You have full visibility into what happened and can implement immediate corrections. We also offer error insurance for enterprise customers.'
              ],
          },
          {
            question: t.industry['How long does implementation take?'],
            answer:
              t.industry[
                'Most financial institutions are live within 2 weeks. That includes compliance review, knowledge base setup, team training, and phased rollout. We work with your timeline and security requirements.'
              ],
          },
          {
            question:
              t.industry['Do you support multiple languages and regions?'],
            answer:
              t.industry[
                'Yes. Agent 5 handles 100+ languages with the same accuracy and compliance standards. Regional data residency options available for EU, US, and other jurisdictions. Localized compliance rules per region.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
